/**
 * Collection grid — Hide/Show Filters + Compare mode surfaces.
 *
 * Three surfaces live here:
 *   1. the Hide/Show Filters toggle (independent of Compare),
 *   2. the Compare mode toggle in the filter toolbar,
 *   3. the per-card checkbox <compare-grid-select> and the sticky
 *      <collection-compare-bar>.
 *
 * All compare STATE lives in window.theme.compare (assets/theme.js). Nothing
 * here reads or writes localStorage, and nothing here talks to another
 * surface: each one mutates the store and repaints from the store's events.
 * The comparison popup is a separate section + script
 * (sections/compare-popup.liquid, assets/compare-popup.js) and is reached
 * only by publishing an open request through the store.
 *
 * OWNERSHIP INVARIANTS
 *   - The mode toggle calls setMode() only — never add/remove/clear.
 *   - The checkbox and the bar call add/remove/clear only — never setMode(),
 *     and never touch the <html data-compare-grid-active> flag.
 *
 * See Blueprint/product-compare/COMPARE_POPUP_SECTION_REARCHITECTURE_PLAN.md
 */
(function () {
  "use strict";

  var FILTERS_KEY = "rovan-filters-collapsed";

  /* ------------------------------------------------------------------ *
   * Hide / Show Filters — independent of Compare, so it is wired up
   * before the store guard below.
   * ------------------------------------------------------------------ */

  function applyFiltersCollapsed(collapsed) {
    var wrapper = document.querySelector(".facets-vertical");
    if (!wrapper) return;
    wrapper.classList.toggle("is-filters-collapsed", collapsed);

    var toggle = document.querySelector("[data-collection-filters-toggle]");
    if (!toggle) return;
    toggle.setAttribute("aria-expanded", String(!collapsed));
    var label = toggle.querySelector(".collection-toolbar__filters-toggle-label");
    if (label) {
      label.textContent = collapsed ? label.dataset.labelHidden : label.dataset.labelShown;
    }
  }

  function restoreFiltersCollapsed() {
    var collapsed = false;
    try {
      collapsed = window.localStorage.getItem(FILTERS_KEY) === "1";
    } catch (error) {
      collapsed = false;
    }
    applyFiltersCollapsed(collapsed);
  }

  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-collection-filters-toggle]");
    if (!toggle) return;
    var wrapper = document.querySelector(".facets-vertical");
    var collapsed = wrapper ? !wrapper.classList.contains("is-filters-collapsed") : false;
    try {
      window.localStorage.setItem(FILTERS_KEY, collapsed ? "1" : "0");
    } catch (error) {
      /* ignore */
    }
    applyFiltersCollapsed(collapsed);
  });

  document.addEventListener("DOMContentLoaded", restoreFiltersCollapsed);
  if (document.readyState !== "loading") restoreFiltersCollapsed();

  /* ------------------------------------------------------------------ *
   * Everything below needs the store. Guard so a partial deploy degrades
   * to "no compare" instead of throwing and taking the filters toggle
   * above down with it.
   * ------------------------------------------------------------------ */

  var store = window.theme && window.theme.compare;
  if (!store) return;

  function escapeHtml(value) {
    var div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  function strings() {
    return window.compareGridStrings || {};
  }

  /* ------------------------------------------------------------------ *
   * Compare mode toggle — delegated, so the switch can appear more than
   * once (e.g. a mobile toolbar instance) and survives an editor
   * re-render without double-binding. Matches ITS OWN selector only, so a
   * card checkbox or Clear all can never flip mode.
   * ------------------------------------------------------------------ */

  document.addEventListener("change", function (event) {
    var toggle = event.target.closest("[data-collection-compare-toggle]");
    if (!toggle) return;
    store.setMode(toggle.checked);
  });

  /** Mirror mode onto every switch (the visual itself is pure CSS). */
  function reflectModeOnToggles(active) {
    document.querySelectorAll("[data-collection-compare-toggle]").forEach(function (input) {
      input.checked = active;
      var label = input.closest(".collection-toolbar__compare-toggle");
      if (label) label.setAttribute("aria-checked", String(active));
    });
  }

  document.addEventListener(store.MODE_EVENT, function (event) {
    reflectModeOnToggles(!!(event.detail && event.detail.active));
  });

  /* Paint the switch from the store now as well as on the store's startup
     broadcast, so a script injected late (theme editor) is still correct. */
  reflectModeOnToggles(store.isActive());

  /* Shared remove control — used by the sticky bar's slots AND the popup's
     column headers, so one delegated listener covers both surfaces. */
  document.addEventListener("click", function (event) {
    var removeBtn = event.target.closest("[data-compare-bar-remove]");
    if (!removeBtn) return;
    store.remove(removeBtn.getAttribute("data-compare-bar-remove"));
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", function () {
      reflectModeOnToggles(store.isActive());
      restoreFiltersCollapsed();
    });
  }

  /* ------------------------------------------------------------------ *
   * Per-card checkbox
   * ------------------------------------------------------------------ */

  if (!window.customElements.get("compare-grid-select")) {
    window.customElements.define(
      "compare-grid-select",
      class CompareGridSelect extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector("input[type=checkbox]");
          if (!this.input) return;

          this.handle = this.dataset.compareHandle;
          this.onChange = this.handleChange.bind(this);
          this.onListChange = this.sync.bind(this);

          this.input.addEventListener("change", this.onChange);
          document.addEventListener(store.LIST_EVENT, this.onListChange);

          /* The editor ships fresh (unchecked) SSR markup on re-render. */
          if (window.Shopify && window.Shopify.designMode) {
            document.addEventListener("shopify:section:load", this.onListChange);
          }

          this.sync();
        }

        disconnectedCallback() {
          document.removeEventListener(store.LIST_EVENT, this.onListChange);
          document.removeEventListener("shopify:section:load", this.onListChange);
        }

        get snapshot() {
          return {
            handle: this.handle,
            id: this.dataset.compareId || "",
            title: this.dataset.compareTitle || "",
            url: this.dataset.compareUrl || "",
            image: this.dataset.compareImage || "",
            price: this.dataset.comparePrice || ""
          };
        }

        handleChange() {
          if (!this.input.checked) {
            store.remove(this.handle);
            return;
          }
          if (store.add(this.snapshot)) return;

          /* Full — revert and flag it. */
          this.input.checked = false;
          this.classList.add("is-compare-full");
          window.setTimeout(
            function (el) {
              el.classList.remove("is-compare-full");
            },
            1600,
            this
          );
        }

        sync() {
          if (!this.input) return;
          var selected = store.isSelected(this.handle);
          this.input.checked = selected;
          this.input.disabled = !selected && store.isFull();
          this.classList.toggle("is-selected", selected);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Sticky comparison bar
   *
   * Desktop: one row, and it slides away as it approaches the footer so
   * the footer can be read/used while comparing.
   * Mobile:  Row A always visible, Row B (thumbnails + Clear all)
   *          collapses under the chevron; the bar never hides and instead
   *          reserves body space equal to its live height.
   * ------------------------------------------------------------------ */

  var FOOTER_GAP = 100; /* px — hide once the bar is this close to the footer */
  var FOOTER_GAP_HYSTERESIS = 8; /* px — stops flapping right at the boundary */

  if (!window.customElements.get("collection-compare-bar")) {
    window.customElements.define(
      "collection-compare-bar",
      class CollectionCompareBar extends HTMLElement {
        connectedCallback() {
          this.slots = this.querySelector("[data-compare-bar-slots]");
          this.countEl = this.querySelector("[data-compare-bar-count]");
          this.openBtn = this.querySelector("[data-compare-bar-open]");
          this.clearBtn = this.querySelector("[data-compare-bar-clear]");
          this.collapseBtn = this.querySelector("[data-compare-bar-collapse]");
          this.collapsible = this.querySelector("[data-compare-bar-collapsible]");
          this.thumbTemplate = this.querySelector("[data-compare-bar-thumb-template]");
          this.max = parseInt(this.dataset.compareMax, 10) || store.MAX;
          this.min = parseInt(this.dataset.compareMin, 10) || store.MIN;
          this.desktopMQ = window.matchMedia("(min-width: 992px)");

          this.onListChange = this.render.bind(this);
          this.onModeChange = this.updateStickyState.bind(this);
          this.onScroll = this.scheduleStickyUpdate.bind(this);
          this.scrollRaf = null;

          document.addEventListener(store.LIST_EVENT, this.onListChange);
          document.addEventListener(store.MODE_EVENT, this.onModeChange);
          window.addEventListener("scroll", this.onScroll, { passive: true });
          window.addEventListener("resize", this.onScroll, { passive: true });

          if (this.clearBtn) {
            this.clearBtn.addEventListener("click", function () {
              /* LIST only — compare mode must stay on. */
              store.clear();
            });
          }
          if (this.openBtn) {
            this.openBtn.addEventListener(
              "click",
              function () {
                if (store.getList().length < this.min) return;
                store.requestOpen();
              }.bind(this)
            );
          }
          if (this.collapseBtn) {
            this.collapseBtn.addEventListener("click", this.toggleCollapse.bind(this));
          }

          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(store.LIST_EVENT, this.onListChange);
          document.removeEventListener(store.MODE_EVENT, this.onModeChange);
          window.removeEventListener("scroll", this.onScroll);
          window.removeEventListener("resize", this.onScroll);
          this.clearReservedSpace();
        }

        /* ---------------------------------------------------- render -- */

        render() {
          if (!this.slots) return;
          var handles = store.getList();

          this.slots.textContent = "";
          for (var i = 0; i < this.max; i += 1) {
            var handle = handles[i];
            this.slots.appendChild(handle ? this.buildThumb(handle) : this.buildPlaceholder());
          }

          if (this.countEl) {
            var template = strings().count || "Compare ({count}/{max})";
            this.countEl.textContent = template
              .replace("{count}", handles.length)
              .replace("{max}", this.max);
          }
          if (this.openBtn) this.openBtn.disabled = handles.length < this.min;

          /* The thumb count can change the bar's height on mobile. */
          this.updateStickyState();
        }

        buildPlaceholder() {
          var slot = document.createElement("div");
          slot.className = "collection-compare-bar__slot is-empty";
          return slot;
        }

        /** Prefer the authored template; build the node directly if an editor
         *  re-mount left the template unqueryable. */
        buildThumb(handle) {
          var snapshot = store.getSnapshot(handle) || { handle: handle };
          if (!this.thumbTemplate) return this.buildThumbFallback(handle, snapshot);

          var fragment = this.thumbTemplate.content.cloneNode(true);
          var slot = fragment.querySelector("[data-compare-bar-slot]");
          if (!slot) return this.buildThumbFallback(handle, snapshot);

          var img = slot.querySelector("[data-compare-bar-thumb-image]");
          if (img) {
            if (snapshot.image) {
              img.src = snapshot.image;
              img.alt = snapshot.title || "";
            } else {
              img.remove();
            }
          }
          var removeBtn = slot.querySelector("[data-compare-bar-remove]");
          if (removeBtn) removeBtn.setAttribute("data-compare-bar-remove", handle);
          return slot;
        }

        buildThumbFallback(handle, snapshot) {
          var slot = document.createElement("div");
          slot.className = "collection-compare-bar__slot is-filled";
          slot.innerHTML =
            '<div class="collection-compare-bar__thumb">' +
            (snapshot.image
              ? '<img src="' +
                escapeHtml(snapshot.image) +
                '" alt="' +
                escapeHtml(snapshot.title || "") +
                '" loading="lazy" width="64" height="64">'
              : "") +
            "</div>" +
            '<button type="button" class="collection-compare-bar__remove" data-compare-bar-remove="' +
            escapeHtml(handle) +
            '" aria-label="' +
            escapeHtml(strings().remove || "Remove") +
            '">&times;</button>';
          return slot;
        }

        /* ---------------------------------------------------- collapse */

        toggleCollapse() {
          var expanded = this.classList.toggle("is-expanded");
          if (this.collapseBtn) {
            this.collapseBtn.setAttribute("aria-expanded", String(expanded));
            var label = expanded
              ? this.collapseBtn.dataset.labelCollapse
              : this.collapseBtn.dataset.labelExpand;
            if (label) this.collapseBtn.setAttribute("aria-label", label);
          }

          /* The bar grows/shrinks as Row B animates: reserve space now
             (rough) and again at transitionend (final height). */
          this.updateStickyState();
          if (this.collapsible) {
            this.collapsible.addEventListener(
              "transitionend",
              this.updateStickyState.bind(this),
              { once: true }
            );
          }
        }

        /* ------------------------------------------------------ sticky */

        scheduleStickyUpdate() {
          if (this.scrollRaf) return;
          this.scrollRaf = window.requestAnimationFrame(
            function () {
              this.scrollRaf = null;
              this.updateStickyState();
            }.bind(this)
          );
        }

        /** Desktop hides near the footer; mobile reserves body space instead. */
        updateStickyState() {
          if (this.desktopMQ.matches) {
            this.clearReservedSpace();
            this.classList.toggle("is-hidden", this.shouldHideForFooter());
            return;
          }
          this.classList.remove("is-hidden");
          this.reserveSpace();
        }

        /**
         * True once the bar's top edge is within FOOTER_GAP of the footer's
         * top edge, so the footer stays fully usable; false again (with a
         * little hysteresis) as soon as the shopper scrolls back up.
         */
        shouldHideForFooter() {
          var footer = document.querySelector(".footer");
          var barHeight = this.offsetHeight;
          /* The bar's distance from the bottom of the viewport. Read from the
             COMPUTED `bottom` (always resolved to px, whatever unit the
             stylesheet used) so assets/collection-compare.css stays the single
             source of that offset. */
          var gap = parseFloat(window.getComputedStyle(this).bottom);
          if (isNaN(gap)) gap = 0;

          if (!footer) {
            /* No footer on this template — fall back to the page bottom. */
            var scrollBottom = window.scrollY + window.innerHeight;
            return scrollBottom >= document.documentElement.scrollHeight - FOOTER_GAP;
          }

          var barTop = window.innerHeight - gap - barHeight;
          var distance = footer.getBoundingClientRect().top - barTop;
          if (this.classList.contains("is-hidden")) {
            return distance <= FOOTER_GAP + FOOTER_GAP_HYSTERESIS;
          }
          return distance <= FOOTER_GAP;
        }

        /** Mobile only: keep the bar from covering the last row of content. */
        reserveSpace() {
          if (!store.isActive()) {
            this.clearReservedSpace();
            return;
          }
          document.documentElement.style.setProperty(
            "--compare-bar-space",
            this.offsetHeight + "px"
          );
        }

        clearReservedSpace() {
          document.documentElement.style.removeProperty("--compare-bar-space");
        }
      }
    );
  }
})();
